import { useMemo, useState } from "react";
import { addWeeks, differenceInCalendarDays, format, parseISO, startOfWeek } from "date-fns";
import { ChevronDown } from "lucide-react";
import clsx from "clsx";
import type { Activity, Deal, Handoff } from "@/api/types";
import { stageColor } from "@/lib/colors";
import { daysSince } from "@/lib/people";
import type { Side } from "@/lib/sides";

const WEEKS = 12;
const COLLAPSE_KEY = "lz-journey-collapsed";

interface Segment {
  stage: string;
  start: string; // ISO day
  days: number;
}

function journeyStart(deal: Deal, activities: Activity[]): string {
  const created = deal.created_at.slice(0, 10);
  const firstTouch = activities
    .filter((a) => a.type !== "status_change")
    .reduce<string | null>((min, a) => (min == null || a.date < min ? a.date : min), null);
  return firstTouch && firstTouch < created ? firstTouch : created;
}

/** Stage history from the status-change log ("From → To"), oldest first. */
function stageSegments(deal: Deal, activities: Activity[], today: string): Segment[] {
  const changes = activities
    .filter((a) => a.type === "status_change" && a.text?.includes("→"))
    .sort((x, y) =>
      x.date === y.date ? (x.created_at < y.created_at ? -1 : 1) : x.date < y.date ? -1 : 1,
    );
  // The story can predate the CRM record (synced or back-dated email), so start
  // from whichever comes first.
  const created = journeyStart(deal, activities);
  const firstStage = changes.length ? changes[0].text!.split("→")[0].trim() : deal.stage;
  const points: { stage: string; start: string }[] = [{ stage: firstStage, start: created }];
  for (const c of changes) points.push({ stage: c.text!.split("→")[1].trim(), start: c.date });
  return points.map((p, i) => {
    const end = points[i + 1]?.start ?? today;
    return { ...p, days: Math.max(0, differenceInCalendarDays(parseISO(end), parseISO(p.start))) };
  });
}

/** Interactions per week for the last WEEKS weeks, split by side. */
function rhythm(activities: Activity[], sides: Map<string, Side>, today: string) {
  const first = startOfWeek(addWeeks(parseISO(today), -(WEEKS - 1)), { weekStartsOn: 1 });
  const weeks = Array.from({ length: WEEKS }, (_, i) => ({
    start: addWeeks(first, i),
    them: 0,
    us: 0,
  }));
  for (const a of activities) {
    if (a.type === "status_change") continue;
    const w = Math.floor(differenceInCalendarDays(parseISO(a.date), first) / 7);
    if (w < 0 || w >= WEEKS) continue;
    const side = sides.get(a.id) ?? "us";
    if (side === "them") weeks[w].them += 1;
    else if (side === "us") weeks[w].us += 1;
    else {
      weeks[w].them += 0.5;
      weeks[w].us += 0.5;
    }
  }
  return weeks;
}

/** The deal at a glance: where it's been, how lively it is, and the key numbers. */
export default function Journey({
  deal,
  activities,
  handoffs,
  sides,
  today,
}: {
  deal: Deal;
  activities: Activity[];
  handoffs: Handoff[];
  sides: Map<string, Side>;
  today: string;
}) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const toggle = () => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? "0" : "1");
      } catch {
        /* storage unavailable */
      }
      return !c;
    });
  };

  const segments = useMemo(() => stageSegments(deal, activities, today), [deal, activities, today]);
  const weeks = useMemo(() => rhythm(activities, sides, today), [activities, sides, today]);
  const total = Math.max(
    1,
    segments.reduce((n, s) => n + s.days, 0),
  );
  const peak = Math.max(1, ...weeks.map((w) => w.them + w.us));
  const interactions = activities.filter((a) => a.type !== "status_change").length;
  const fromThem = activities.filter((a) => sides.get(a.id) === "them").length;
  const ageDays = differenceInCalendarDays(
    parseISO(today),
    parseISO(journeyStart(deal, activities)),
  );
  const quiet = daysSince(deal.last_contact);
  const accepted = handoffs.filter((h) => h.status === "accepted").length;

  const stats: { label: string; value: string; warn?: boolean }[] = [
    { label: "In pipeline", value: `${ageDays} d` },
    { label: "In this stage", value: deal.days_in_stage != null ? `${deal.days_in_stage} d` : "—" },
    {
      label: "Interactions",
      value: `${interactions}${interactions ? ` · ${fromThem} from them` : ""}`,
    },
    {
      label: "Last contact",
      value: quiet == null ? "never" : quiet === 0 ? "today" : `${quiet} d ago`,
      warn: quiet == null || quiet >= 14,
    },
    ...(accepted ? [{ label: "Handoffs", value: String(accepted) }] : []),
  ];

  return (
    <section className="mx-4 md:mx-6 mb-3 rounded-2xl border border-fg/[0.07] bg-ink-900">
      <button className="flex w-full items-center gap-2 px-4 py-2.5 text-left" onClick={toggle}>
        <span className="section-title">Journey</span>
        {collapsed && (
          <span className="truncate text-sm text-mut-500">
            {segments.length} {segments.length === 1 ? "stage" : "stages"} · {interactions}{" "}
            interactions · last contact {stats[3].value}
          </span>
        )}
        <ChevronDown
          className={clsx(
            "ml-auto h-4 w-4 text-mut-500 transition-transform",
            !collapsed && "rotate-180",
          )}
        />
      </button>

      {!collapsed && (
        <div className="space-y-4 px-4 pb-4">
          {/* Stage timeline: each stage's width is the time spent in it. */}
          <div>
            <div className="flex h-7 w-full overflow-hidden rounded-lg">
              {segments.map((s, i) => {
                const current = i === segments.length - 1;
                return (
                  <div
                    key={`${s.stage}-${s.start}`}
                    title={`${s.stage}: ${s.days} days from ${format(parseISO(s.start), "d MMM")}`}
                    className="relative flex min-w-[28px] items-center px-2 text-2xs font-semibold text-white"
                    style={{
                      // Proportional to time in stage, with a floor so short stages stay readable.
                      flex: `${Math.max(s.days, total * 0.06)} 1 0`,
                      background: stageColor(s.stage),
                      opacity: current ? 1 : 0.75,
                    }}
                  >
                    <span className="truncate">{s.stage}</span>
                    <span className="ml-auto pl-1 opacity-80">{s.days}d</span>
                    {current && (
                      <span className="absolute inset-y-0 right-0 w-1 animate-pulse bg-white/60" />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-1 flex justify-between text-2xs text-mut-500">
              <span>{format(parseISO(segments[0].start), "d MMM yyyy")}</span>
              <span>today</span>
            </div>
          </div>

          {/* Weekly rhythm: who's been talking, week by week. */}
          <div>
            <div className="mb-1 flex items-center gap-3 text-2xs text-mut-500">
              <span>Activity, last {WEEKS} weeks</span>
              <span className="ml-auto flex items-center gap-1">
                <span className="h-2 w-2 rounded-sm bg-accent-violet" /> Us
              </span>
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-sm bg-accent-cyan" /> Them
              </span>
            </div>
            <div className="flex h-14 items-end gap-1">
              {weeks.map((w) => {
                const n = w.them + w.us;
                return (
                  <div
                    key={w.start.toISOString()}
                    className="flex h-full flex-1 flex-col justify-end"
                    title={`Week of ${format(w.start, "d MMM")}: ${Math.round(w.us)} from us, ${Math.round(w.them)} from them`}
                  >
                    {n === 0 ? (
                      <div className="h-[3px] rounded-full bg-fg/[0.08]" />
                    ) : (
                      <>
                        <div
                          className="rounded-t-sm bg-accent-cyan"
                          style={{ height: `${(w.them / peak) * 100}%` }}
                        />
                        <div
                          className={clsx("bg-accent-violet", w.them === 0 && "rounded-t-sm")}
                          style={{ height: `${(w.us / peak) * 100}%` }}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-1 flex justify-between text-2xs text-mut-500">
              <span>{format(weeks[0].start, "d MMM")}</span>
              <span>this week</span>
            </div>
          </div>

          {/* Key numbers */}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
            {stats.slice(0, 4).map((s) => (
              <div key={s.label}>
                <dt className="text-2xs text-mut-500">{s.label}</dt>
                <dd className={clsx("text-md font-semibold", s.warn && "text-accent-amber")}>
                  {s.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </section>
  );
}
